/**
 * Personalized data service - sorting utilities only
 */

import { PersonalizedLeaderboardEntry } from '../types';

/**
 * Sort personalized leaderboard by metric
 * @param entries - Personalized leaderboard entries
 * @param metric - Metric to sort by
 * @returns Sorted entries
 */
export const sortPersonalizedLeaderboard = (
  entries: PersonalizedLeaderboardEntry[],
  metric: keyof PersonalizedLeaderboardEntry
): PersonalizedLeaderboardEntry[] => {
  return [...entries].sort((a, b) => {
    const aValue = a[metric];
    const bValue = b[metric];
    
    // Handle numeric values
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return bValue - aValue; // Descending order
    }
    
    // Handle string values
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.localeCompare(bValue);
    }
    
    return 0;
  });
};

/**
 * Calculate efficiency score
 * @param linesAdded - Lines of code added
 * @param timeMinutes - Time spent in minutes
 * @returns Efficiency score (lines per hour)
 */
export const calculateEfficiencyScore = (linesAdded: number, timeMinutes: number): number => {
  if (timeMinutes === 0) return 0;
  return (linesAdded / timeMinutes) * 60; // Convert to lines per hour
};

/**
 * Format time in minutes to readable format
 * @param minutes - Time in minutes
 * @returns Formatted time string
 */
export const formatTimeMinutes = (minutes: number): string => {
  if (minutes === 0) return '-';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) {
    return `${mins}m`;
  } else if (mins === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${mins}m`;
  }
};

/**
 * Format time in decimal hours to readable format (hours and minutes)
 * @param decimalHours - Time in decimal hours (e.g., 2.5 for 2 hours 30 minutes)
 * @returns Formatted time string
 */
export const formatDecimalHours = (decimalHours: number): string => {
  if (decimalHours === 0) return '-';
  
  // Convert decimal hours to minutes
  const totalMinutes = Math.round(decimalHours * 60);
  return formatTimeMinutes(totalMinutes);
}; 